import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Stack, Progress, Container, Alert, Stepper, Skeleton, Group } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { MealPlan, MealPlanPreferences, PlanningMode } from '../../types/mealPlan';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { useMealPlanning } from '../../hooks/useMealPlanning';
import { useAuth } from '../../hooks/useAuth';
import { useAppUser } from '../../hooks/useAppUser';
import { getPantryItems } from '../../services/pantryService';
import { WeekShapeStep } from './steps/WeekShapeStep';
import { ModeStep } from './steps/ModeStep';
import { PickerStep, PickerValue } from './steps/PickerStep';
import {
  PickerCandidatesCache,
  buildPickerRequest,
  fetchPickerCandidates,
} from '../../utils/pickerCandidates';
import { ExtrasStep } from './steps/ExtrasStep';
import { ReviewCreateStep } from './steps/ReviewCreateStep';
import { householdSegment } from '../../utils/householdSegment';
import { trackMealPlanGenerationReddit } from '../../utils/redditPixel';
import { trackMealPlanGenerationMeta } from '../../utils/metaPixel';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../utils/analytics';
import classes from './MealPlanningWizard.module.css';

type MealPlanningWizardProps = {
  onComplete: (planId: string, failed?: boolean) => void;
  onCancel: () => void;
};

type WizardStep = {
  id: 'week-shape' | 'mode' | 'picker' | 'extras' | 'review-create';
  title: string;
};

// How long the week-shape inputs must hold still before the picker shortlist
// is warmed. Long enough that dragging across a date range fires one request
// rather than one per day touched, short enough to still be finished by the
// time a user who accepts the defaults reads the step and moves on.
const PICKER_PREFETCH_SETTLE_MS = 800;

const getTotalDays = (dateRange: [string | null, string | null]): number => {
  if (!dateRange[0] || !dateRange[1]) return 7;
  const diffTime = Math.abs(
    new Date(dateRange[1]).getTime() - new Date(dateRange[0]).getTime()
  );
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

export const MealPlanningWizard = ({ onComplete, onCancel }: MealPlanningWizardProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highestStepReached, setHighestStepReached] = useState(0);
  // Extras + week shape live in preferences; delegation lives in mode/picks
  // and is only folded into recipeSources/aiRecipeDays at submit time
  const [preferences, setPreferences] = useState<MealPlanPreferences>({
    recipeSources: 'ai_only',
  });
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [mode, setMode] = useState<PlanningMode | null>(null);
  const [noveltyDays, setNoveltyDays] = useState(0);
  const [picks, setPicks] = useState<PickerValue>({
    lockedRecipeIds: [],
    lockedCatalogRecipeIds: [],
  });
  // Survives picker unmount so back/forward navigation doesn't refetch
  const [pickerCache, setPickerCache] = useState<PickerCandidatesCache | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [pantryStats, setPantryStats] = useState<{ staples: number; ingredients: number }>({ staples: 0, ingredients: 0 });
  // Offered on the extras step behind a tap, never pre-filled. Fetched once
  // here rather than in the step so back/forward navigation doesn't refetch.
  const [lastCustomRequest, setLastCustomRequest] = useState<string | null>(null);

  const { createMealPlan, getLastCustomRequest, loading, error } = useMealPlanning();
  const { user } = useAuth();
  const { user: appUser, loading: appUserLoading } = useAppUser();
  const posthog = usePostHog();
  const modePreselected = useRef(false);

  const pickedCount = picks.lockedRecipeIds.length + picks.lockedCatalogRecipeIds.length;

  // One "shortlist shown" impression per distinct shortlist per wizard
  // session. Held here rather than in the picker step because the step
  // unmounts on every back/forward, and because the shortlist it displays may
  // have been fetched by the prefetch below instead of by the step itself.
  const shownShortlistKeys = useRef<Set<string>>(new Set());
  const claimShortlistShown = useCallback((cacheKey: string) => {
    if (shownShortlistKeys.current.has(cacheKey)) return false;
    shownShortlistKeys.current.add(cacheKey);
    return true;
  }, []);

  // First-timer = has never created a plan, INCLUDING plans since deleted.
  // lastPlanCreatedAt is denormalized onto the user doc at every create and
  // survives deletion, so it's the durable "has ever made a plan" signal
  // (plans.length would wrongly reset for made-then-deleted users).
  const isFirstTimer = !appUserLoading && !!appUser && !appUser.lastPlanCreatedAt;

  // First-timers skip the mode step and land in the picker — the picker IS
  // their first-plan experience (calibration); returning users get the mode
  // step with their last mode remembered
  // A fully penciled week has nothing to generate, so mode, picker, and
  // extras (all generation inputs) drop out and the wizard goes straight to
  // review
  const fullyPenciled = (() => {
    const totalDays = getTotalDays(dateRange);
    const penciledCount = preferences.penciledMeals?.length ?? 0;
    return totalDays > 0 && penciledCount >= totalDays;
  })();

  const steps = useMemo<WizardStep[]>(() => {
    const list: WizardStep[] = [{ id: 'week-shape', title: 'Your week' }];
    if (!fullyPenciled) {
      if (!isFirstTimer) {
        list.push({ id: 'mode', title: 'Your mix' });
      }
      if (isFirstTimer || mode === 'pick_a_few') {
        list.push({ id: 'picker', title: 'Your picks' });
      }
      list.push({ id: 'extras', title: 'Your extras' });
    }
    list.push({ id: 'review-create', title: 'Review' });
    return list;
  }, [isFirstTimer, mode, fullyPenciled]);

  const currentStep = steps[Math.min(currentStepIndex, steps.length - 1)];
  const isLastStep = currentStepIndex === steps.length - 1;
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Funnel instrumentation: fires once per distinct step view (keyed by id so
  // the dynamic step list can't double-fire the same view)
  const lastViewedStepId = useRef<string | null>(null);
  useEffect(() => {
    if (!initialized || !currentStep) return;
    if (lastViewedStepId.current === currentStep.id) return;
    lastViewedStepId.current = currentStep.id;
    posthog?.capture(AnalyticsEvent.WizardStepViewed, {
      step_id: currentStep.id,
      step_index: currentStepIndex,
    });
  }, [currentStep, currentStepIndex, initialized, posthog]);

  // Initialize: pantry stats and the household's last custom request
  useEffect(() => {
    const initializeWizard = async () => {
      if (user) {
        try {
          const token = await user.getIdToken();
          const response = await getPantryItems(token);
          const staples = response.items.filter(item => item.type === 'staple').length;
          const ingredients = response.items.filter(item => item.type === 'ingredient').length;
          setPantryStats({ staples, ingredients });
        } catch (error) {
          console.warn('Failed to check pantry items:', error);
          setPantryStats({ staples: 0, ingredients: 0 });
        }
        // Best effort: the offer is a convenience, never a blocker
        getLastCustomRequest().then(setLastCustomRequest);
      }
      setInitialized(true);
    };

    initializeWizard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Pre-select the remembered mode for returning users (one-shot)
  useEffect(() => {
    if (modePreselected.current || mode !== null) return;
    const remembered = appUser?.familyProfile?.planningMode;
    if (remembered) {
      modePreselected.current = true;
      setMode(remembered);
    }
  }, [appUser, mode]);

  // If the user goes back and shrinks the week below their pick count — by
  // shortening the range or penciling in more nights — trim picks from the
  // end so a stale lock can't ride along in the submit
  useEffect(() => {
    const cookingDays = Math.max(
      1,
      getTotalDays(dateRange) - (preferences.penciledMeals?.length ?? 0)
    );
    const mealsNeeded =
      preferences.uniqueMeals !== undefined
        ? Math.min(preferences.uniqueMeals, cookingDays)
        : cookingDays;
    if (pickedCount > mealsNeeded) {
      let overflow = pickedCount - mealsNeeded;
      setPicks(prev => {
        const catalogTrim = Math.min(overflow, prev.lockedCatalogRecipeIds.length);
        overflow -= catalogTrim;
        return {
          lockedCatalogRecipeIds: prev.lockedCatalogRecipeIds.slice(
            0,
            prev.lockedCatalogRecipeIds.length - catalogTrim
          ),
          lockedRecipeIds: prev.lockedRecipeIds.slice(
            0,
            prev.lockedRecipeIds.length - overflow
          ),
        };
      });
    }
  }, [dateRange, preferences.uniqueMeals, preferences.penciledMeals, pickedCount]);

  // Warm the picker's shortlist before the step opens. The request behind it
  // runs vector retrieval over the catalog and takes seconds, while every
  // input to it — the week's dates and the meal-prep shape — is settled back
  // on the week-shape step. Left unwarmed, the picker starts that request on
  // mount and the user watches a skeleton for the whole of it.
  //
  // While the user is still ON week-shape those inputs are a moving target,
  // so the request waits for them to settle; once they are behind the user it
  // fires immediately. Identical keys are deduped inside
  // fetchPickerCandidates, so re-advancing (or the picker step mounting into
  // an open request) never opens a second one.
  const [startDate, endDate] = dateRange;
  const prefetchPrepNotes = preferences.mealPrepPreferences?.generalPreferences;
  useEffect(() => {
    const pickerIndex = steps.findIndex((step) => step.id === 'picker');
    // Only worth warming while the picker is in the flow and still ahead of
    // the user — once it's the current step, the step owns the fetch
    if (pickerIndex < 0 || pickerIndex <= currentStepIndex) return;
    if (!user || !startDate) return;
    const request = buildPickerRequest({
      dateRange: [startDate, endDate],
      uniqueMeals: preferences.uniqueMeals,
      mealPrepPreferences: prefetchPrepNotes,
    });
    if (pickerCache?.key === request.cacheKey) return;
    let cancelled = false;
    const timer = setTimeout(
      () => {
        fetchPickerCandidates(user, request)
          .then((data) => {
            if (!cancelled) setPickerCache({ key: request.cacheKey, data });
          })
          // A failed warm-up isn't the user's problem here — the picker step
          // runs the same request on mount and owns the retry UI
          .catch(() => {});
      },
      currentStepIndex === 0 ? PICKER_PREFETCH_SETTLE_MS : 0
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Deliberately keyed on primitives, not on the `dateRange` array or the
    // preferences object: week-shape re-populates both from the profile on
    // mount, and a fresh identity carrying identical values would restart the
    // settle timer instead of letting it fire.
  }, [
    steps,
    currentStepIndex,
    user,
    startDate,
    endDate,
    preferences.uniqueMeals,
    prefetchPrepNotes,
    pickerCache,
  ]);

  const handleNext = () => {
    if (isLastStep) {
      // Review step handles creation
      return;
    }
    posthog?.capture(AnalyticsEvent.WizardStepCompleted, {
      step_id: currentStep.id,
      step_index: currentStepIndex,
    });
    const nextStepIndex = currentStepIndex + 1;
    setCurrentStepIndex(nextStepIndex);
    setHighestStepReached(Math.max(highestStepReached, nextStepIndex));
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      window.scrollTo(0, 0);
    } else {
      posthog?.capture(AnalyticsEvent.WizardCancelled, {
        step_id: currentStep.id,
        step_index: currentStepIndex,
      });
      onCancel();
    }
  };

  // The mode the plan is effectively built with (zero picks = surprise-me)
  const getEffectiveMode = (): PlanningMode => {
    const inPickerFlow = isFirstTimer || mode === 'pick_a_few';
    if (inPickerFlow) {
      return pickedCount > 0 ? 'pick_a_few' : 'surprise_me';
    }
    return mode ?? 'surprise_me';
  };

  // Fold mode + picks + novelty into the legacy recipeSources/aiRecipeDays
  // contract the backend expects
  const derivePlanPreferences = (): MealPlanPreferences => {
    // Penciled-in nights are covered by the family, not by recipes. Clamp
    // uniqueMeals too: penciling nights after setting the meal-prep slider
    // must not submit more prepped meals than there are cooking days.
    const penciledCount = preferences.penciledMeals?.length ?? 0;
    const cookingDays = Math.max(0, getTotalDays(dateRange) - penciledCount);
    // Every night penciled: mode, picks, and meal prep are all generation
    // inputs with nothing to act on, and stale ones (locked picker recipes, a
    // uniqueMeals from before the nights were penciled) would trip backend
    // gates
    if (cookingDays === 0) {
      return {
        ...preferences,
        recipeSources: 'ai_only',
        aiRecipeDays: undefined,
        uniqueMeals: undefined,
        mealPrepPreferences: undefined,
        lockedRecipeIds: undefined,
        lockedCatalogRecipeIds: undefined,
        planningMode: undefined,
        noveltyDays: undefined,
      };
    }
    const clampedUniqueMeals =
      preferences.uniqueMeals !== undefined && preferences.uniqueMeals > cookingDays
        ? Math.max(1, cookingDays)
        : preferences.uniqueMeals;
    const mealsNeeded = clampedUniqueMeals ?? cookingDays;
    const base: MealPlanPreferences = {
      ...preferences,
      uniqueMeals: clampedUniqueMeals,
      lockedRecipeIds: undefined,
      lockedCatalogRecipeIds: undefined,
      aiRecipeDays: undefined,
      // Display metadata persisted on the plan so post-creation surfaces
      // (e.g. the preferences drawer) can speak modes, not the legacy contract
      planningMode: getEffectiveMode(),
      noveltyDays: undefined,
    };

    switch (getEffectiveMode()) {
      case 'pick_a_few': {
        const aiDays = Math.max(0, mealsNeeded - pickedCount);
        return {
          ...base,
          recipeSources: aiDays === 0 ? 'my_recipes_only' : 'mixed',
          aiRecipeDays: aiDays === 0 ? undefined : aiDays,
          lockedRecipeIds: picks.lockedRecipeIds.length ? picks.lockedRecipeIds : undefined,
          lockedCatalogRecipeIds: picks.lockedCatalogRecipeIds.length
            ? picks.lockedCatalogRecipeIds
            : undefined,
        };
      }
      case 'shuffle_cookbook':
        return {
          ...base,
          recipeSources: noveltyDays > 0 ? 'mixed' : 'my_recipes_only',
          aiRecipeDays: noveltyDays > 0 ? noveltyDays : undefined,
          noveltyDays: noveltyDays > 0 ? noveltyDays : undefined,
        };
      case 'surprise_me':
      default:
        return { ...base, recipeSources: 'ai_only' };
    }
  };

  const handleCreatePlan = async () => {
    const [startDate, endDate] = dateRange;

    if (!startDate || !endDate) {
      notifications.show({
        title: 'Invalid date range',
        message: 'Please select both start and end dates for your meal plan.',
        color: 'red',
        autoClose: 4000,
      });
      return;
    }

    const finalPreferences = derivePlanPreferences();

    // If quickMealDays is undefined (user never opened the extras), initialize
    // it with all days to indicate all days should be quick
    if (finalPreferences.quickMealDays === undefined) {
      const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
      const daysDiff = getTotalDays(dateRange);

      const orderedDays = [];
      for (let i = 0; i < daysDiff; i++) {
        const currentDate = new Date(startYear, startMonth - 1, startDay + i);
        const dayOfWeek = currentDate.getDay();
        const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        orderedDays.push(DAYS_OF_WEEK[dayIndex]);
      }

      finalPreferences.quickMealDays = orderedDays;
    }

    const mealPlan = await createMealPlan(finalPreferences, startDate, endDate);
    if (mealPlan) {
      const failed = mealPlan.generationStatus?.status === 'failed';

      // Seed the shared plans cache with the new in-flight plan so the
      // dashboard's first paint shows its row and progress immediately — the
      // cache seed is also what arms the dashboard's Firestore progress
      // listener (it only enables itself when the plan list it can see has
      // something pending). Only when a cache exists: seeding a cold cache
      // with just this plan would paint a one-row dashboard and let the older
      // plans pop in behind it.
      if (user) {
        const cacheKey = `meal-plans:${user.uid}`;
        const cached = getPageCache<MealPlan[]>(cacheKey);
        if (cached) {
          setPageCache(cacheKey, [
            // A fail-fast rejection returns a plan with no assignments at
            // all; normalize so cache consumers can count on the array
            { ...mealPlan, assignments: mealPlan.assignments ?? [] },
            ...cached.filter((plan) => plan.id !== mealPlan.id),
          ]);
        }
      }

      // Show success notification only if not failed
      if (!failed) {
        notifications.show({
          title: 'Meal plan submitted',
          message: 'Your personalized meal plan is being created. You can view progress on the dashboard.',
          color: 'blue',
          autoClose: 6000,
        });

        // Track meal plan generation
        if (user) {
          const recipeCount = getTotalDays(dateRange);

          trackMealPlanGenerationReddit(user.uid, mealPlan.id, recipeCount);
          trackMealPlanGenerationMeta(user.uid, mealPlan.id, recipeCount);

          posthog?.capture(AnalyticsEvent.MealPlanCreated, {
            meal_plan_id: mealPlan.id,
            total_days: recipeCount,
            unique_meals: finalPreferences.uniqueMeals ?? recipeCount,
            meal_prep: finalPreferences.uniqueMeals !== undefined,
            recipe_sources: finalPreferences.recipeSources,
            ai_recipe_days: finalPreferences.aiRecipeDays,
            planning_mode: getEffectiveMode(),
            first_plan: isFirstTimer,
            locked_count: picks.lockedRecipeIds.length,
            locked_catalog_count: picks.lockedCatalogRecipeIds.length,
            novelty_days: mode === 'shuffle_cookbook' ? noveltyDays : undefined,
            event_count: finalPreferences.events?.length ?? 0,
            has_custom_requests: !!finalPreferences.customRequests,
            use_pantry: !!finalPreferences.usePantryIngredients,
            includes_breakfast: !!finalPreferences.additionalMeals?.breakfast,
            includes_lunch: !!finalPreferences.additionalMeals?.lunch,
            lunch_mode: finalPreferences.additionalMeals?.lunch?.mode,
          });
        }

        // Remember the chosen mode (and meal prep prefs) for the next plan.
        // A first-timer's implicit flow counts too: what they did IS the mode.
        if (user) {
          try {
            const token = await user.getIdToken();
            const chosenMode: PlanningMode =
              mode ?? (pickedCount > 0 ? 'pick_a_few' : 'surprise_me');
            // First lunch this household ever planned: their answer to the
            // wizard's inline leftovers-vs-fresh chooser becomes the standing
            // default (the chooser's copy promises this). Skipped when the
            // week has under two cook nights (single-day, or penciled down),
            // where rotation is forced rather than chosen.
            // Spread the existing cookingStyle: the PATCH replaces the whole
            // object, and a sparse body would wipe the other traits.
            const existingCookingStyle = appUser?.familyProfile?.cookingStyle;
            const chosenLunchMode = finalPreferences.additionalMeals?.lunch?.mode;
            const rememberLunchMode =
              chosenLunchMode !== undefined &&
              existingCookingStyle?.lunchMode === undefined &&
              getTotalDays(dateRange) -
                (finalPreferences.penciledMeals?.length ?? 0) >
                1;
            await fetch(`${process.env.REACT_APP_API_URL}/api/user/family-profile`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                planningMode: chosenMode,
                ...(finalPreferences.mealPrepPreferences
                  ? { mealPrepPreferences: finalPreferences.mealPrepPreferences }
                  : {}),
                ...(rememberLunchMode
                  ? { cookingStyle: { ...existingCookingStyle, lunchMode: chosenLunchMode } }
                  : {}),
              }),
            });
          } catch (error) {
            console.error('Failed to save planning preferences to profile:', error);
            // Don't throw - meal plan creation succeeded, this is just a nice-to-have
          }
        }
      }

      // Pass failure status so parent can handle navigation appropriately
      onComplete(mealPlan.id, failed);
    }
  };

  const updatePreferences = (updates: Partial<MealPlanPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  };

  // Show a skeleton of the first step while initializing (profile load
  // included — the step list depends on first-timer detection, so it must not
  // change shape mid-flow)
  if (!initialized || appUserLoading) {
    return (
      <Container size="sm" py="xl" px={0}>
        <Stack gap="xl">
          {/* Step indicator */}
          <Skeleton height={8} radius="xl" hiddenFrom="sm" />
          <Group justify="space-between" visibleFrom="sm" px="md">
            {Array.from({ length: 4 }).map((_, i) => (
              <Group key={i} gap="xs">
                <Skeleton height={24} circle />
                <Skeleton height={10} width={48} />
              </Group>
            ))}
          </Group>

          {/* Step header */}
          <Stack gap="md" align="center" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
            <Skeleton height={80} circle />
            <Skeleton height={28} width={240} radius="sm" />
            <Skeleton height={16} width="min(320px, 85%)" radius="sm" />

            {/* Section cards */}
            <Skeleton height={180} radius="lg" mt="md" />
            <Skeleton height={140} radius="lg" />

            {/* Navigation */}
            <Group justify="space-between" w="100%" pt="md" wrap="nowrap" className="wizard-footer">
              <Skeleton height={40} width={90} radius="sm" />
              <Skeleton height={40} width={120} radius="sm" />
            </Group>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl" px={0}>
      <Stack gap="xl">
        {/* Step Indicator - Responsive */}
        <>
          {/* Mobile: Simple Progress Bar */}
          <Progress
            value={progress}
            size="sm"
            color="orange"
            radius="xl"
            hiddenFrom="sm"
          />

          {/* Desktop: Interactive Stepper */}
          <Stepper
            active={currentStepIndex}
            color="orange"
            size="xs"
            orientation="horizontal"
            allowNextStepsSelect={false}
            onStepClick={(stepIndex) => {
              // Allow navigation to any step up to the highest reached
              if (stepIndex <= highestStepReached) {
                setCurrentStepIndex(stepIndex);
                window.scrollTo(0, 0);
              }
            }}
            classNames={{ stepIcon: classes.stepIcon }}
            styles={{
              step: { fontSize: '12px' },
              stepLabel: {
                fontSize: '11px',
                fontWeight: 400,
                color: 'var(--mantine-color-gray-6)'
              },
              stepDescription: { display: 'none' }
            }}
            visibleFrom="sm"
          >
            {steps.map((step) => (
              <Stepper.Step key={step.id} label={step.title} />
            ))}
          </Stepper>
        </>

        {/* Error Alert */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
            {error}
          </Alert>
        )}

        {/* Current step */}
        {currentStep.id === 'week-shape' && (
          <WeekShapeStep
            value={{
              dateRange,
              uniqueMeals: preferences.uniqueMeals,
              mealPrepPreferences: preferences.mealPrepPreferences,
              quickMealDays: preferences.quickMealDays,
              additionalMeals: preferences.additionalMeals,
              prepDays: preferences.prepDays,
              penciledMeals: preferences.penciledMeals,
            }}
            onChange={(value) => {
              setDateRange(value.dateRange);
              updatePreferences({
                uniqueMeals: value.uniqueMeals,
                mealPrepPreferences: value.mealPrepPreferences,
                quickMealDays: value.quickMealDays,
                additionalMeals: value.additionalMeals,
                prepDays: value.prepDays,
                penciledMeals: value.penciledMeals,
              });
            }}
            onNext={handleNext}
            onBack={handleBack}
            plannedEvents={preferences.events}
          />
        )}
        {currentStep.id === 'mode' && (
          <ModeStep
            value={{ mode, noveltyDays }}
            onChange={(value) => {
              setMode(value.mode);
              setNoveltyDays(value.noveltyDays);
            }}
            onNext={handleNext}
            onBack={handleBack}
            dateRange={dateRange}
            uniqueMeals={preferences.uniqueMeals}
          />
        )}
        {currentStep.id === 'picker' && (
          <PickerStep
            value={picks}
            onChange={setPicks}
            onNext={handleNext}
            onBack={handleBack}
            dateRange={dateRange}
            uniqueMeals={preferences.uniqueMeals}
            penciledCount={preferences.penciledMeals?.length ?? 0}
            mealPrepPreferences={preferences.mealPrepPreferences?.generalPreferences}
            candidatesCache={pickerCache}
            onCandidatesLoaded={setPickerCache}
            claimShortlistShown={claimShortlistShown}
            userDietaryRestrictions={appUser?.familyProfile?.dietaryRestrictions ?? []}
            householdSegment={householdSegment(appUser?.familyProfile)}
          />
        )}
        {currentStep.id === 'extras' && (
          <ExtrasStep
            value={{
              customRequests: preferences.customRequests,
              events: preferences.events,
              usePantryIngredients: preferences.usePantryIngredients,
            }}
            onChange={(value) =>
              updatePreferences({
                customRequests: value.customRequests,
                events: value.events,
                usePantryIngredients: value.usePantryIngredients,
              })
            }
            onNext={handleNext}
            onBack={handleBack}
            dateRange={dateRange}
            uniqueMeals={preferences.uniqueMeals}
            pantryStats={pantryStats}
            penciledMeals={preferences.penciledMeals}
            lastCustomRequest={lastCustomRequest}
          />
        )}
        {currentStep.id === 'review-create' && (
          <ReviewCreateStep
            preferences={preferences}
            dateRange={dateRange}
            onCreatePlan={handleCreatePlan}
            onBack={handleBack}
            loading={loading}
            wizardMeta={{
              mode: getEffectiveMode(),
              noveltyDays,
              lockedCount: pickedCount,
            }}
          />
        )}
      </Stack>
    </Container>
  );
};
